/**
 * <copy-to-clipboard> Web Component
 *
 * @attr {string} content - The text to copy to the clipboard.
 *
 * Renders a self-contained button. States:
 * - ready  (default): clipboard glyph + "Copy"
 * - copied: check glyph + "Copied"
 * - error : warning glyph + "Error"
 */
class CopyToClipboardElement extends HTMLElement {
  static get observedAttributes() {
    return ['content'];
  }

  static RESET_DELAY_MS = 1500;

  static STATES = {
    ready: { icon: 'copy', label: 'Copy', modifier: '' },
    copied: { icon: 'check', label: 'Copied', modifier: 'is-copied' },
    error: { icon: 'warning', label: 'Error', modifier: 'is-error' },
  };

  constructor() {
    super();
    this._state = 'ready';
    this._resetTimer = null;
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._render();
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    if (this._resetTimer) {
      clearTimeout(this._resetTimer);
      this._resetTimer = null;
    }
  }

  attributeChangedCallback() {
    // No-op for now; we always read `content` at click time.
  }

  get content() {
    return this.getAttribute('content') || '';
  }

  set content(value) {
    if (value === null || value === undefined) {
      this.removeAttribute('content');
      return;
    }

    this.setAttribute('content', value);
  }

  async _onClick(event) {
    const button = event.target?.closest?.('button');
    if (!button) return;

    await this.copy();
  }

  async copy() {
    try {
      await this._writeText(this.content);
      this._setState('copied');
    } catch {
      this._setState('error');
    } finally {
      if (this._resetTimer)
        clearTimeout(this._resetTimer);

      this._resetTimer = setTimeout(() => {
        this._setState('ready');
      }, CopyToClipboardElement.RESET_DELAY_MS);
    }
  }

  async _writeText(text) {
    if (!navigator.clipboard?.writeText)
      throw new Error('Clipboard API unavailable');

    return await navigator.clipboard.writeText(text);
  }

  _setState(state) {
    this._state = state;
    this._render();
  }

  _render() {
    const { icon, label, modifier } = CopyToClipboardElement.STATES[this._state];
    const className = modifier ? `copy-to-clipboard-btn ${modifier}` : 'copy-to-clipboard-btn';

    this.innerHTML =
      `<button type="button" class="${className}">` +
      `<i class="icon-${icon}" aria-hidden="true"></i>${label}` +
      '</button>';
  }
}

if (!customElements.get('copy-to-clipboard'))
  customElements.define('copy-to-clipboard', CopyToClipboardElement);

export { CopyToClipboardElement };
